/**
 * React bindings for the organization scope.
 *
 * Kept apart from `orgScope.ts` so that module stays framework-free and cheap to
 * unit test, and apart from the barrel so importing a hook does not pull the
 * auth store into every store's module graph.
 */

import { useState } from 'react';

import { useAuthStoreV1 } from '@/stores/authStore_v1';

import type { OrgId } from './types';

/**
 * The active organization's id, as a reactive value.
 *
 * Use it to scope a React Query key. Almost every request in this app carries
 * the active organization implicitly — `skillsAuthHeaders()` and
 * `testingAuthHeaders()` attach `X-Organization-ID` from the auth store — so a
 * query key that omits the org describes a response it cannot uniquely
 * identify, and React Query will happily serve Org A's list to Org B from cache.
 *
 * ```ts
 * const orgId = useActiveOrgId();
 * useQuery({ queryKey: agentKeys.list(orgId, params), queryFn: () => getAgents(params) });
 * ```
 */
export function useActiveOrgId(): OrgId | null {
  return useAuthStoreV1((state) => state.selectedOrg?.id ?? null);
}

/** What {@link useOrgScopeKey} remembers between renders. */
export interface OrgScopeKeyState {
  /** The last real organization seen. A render with no org leaves it alone. */
  lastOrgId: OrgId | null;
  /** How many times one real organization has replaced another. */
  generation: number;
}

/**
 * The next scope key state, given the organization on screen now.
 *
 * Only an org replacing a DIFFERENT org is a switch. The first org arriving after a
 * page load (the user fetch resolving) is not: treating it as one would mount every
 * page twice on each refresh. Nor is a render with no org (loading, signing out).
 */
export function nextOrgScopeKey(state: OrgScopeKeyState, orgId: OrgId | null): OrgScopeKeyState {
  if (!orgId || orgId === state.lastOrgId) return state;
  if (!state.lastOrgId) return { lastOrgId: orgId, generation: state.generation };
  return { lastOrgId: orgId, generation: state.generation + 1 };
}

/**
 * A key that changes on every org switch, and only then.
 *
 * Give it to a subtree (`<Fragment key={useOrgScopeKey()}>`) and a switch mounts that
 * subtree afresh: view state held in `useState` (an open item, a result, a selection)
 * cannot carry one org's data into the next. App.tsx does this for every protected
 * route (`routes/orgScopedRoute.ts`).
 *
 * Only for view state. Work the user expects to find again on returning belongs in an
 * org-partitioned store (see `orgPartition.ts`), not here.
 */
export function useOrgScopeKey(): number {
  const orgId = useActiveOrgId();
  const [state, setState] = useState<OrgScopeKeyState>(() => ({ lastOrgId: orgId, generation: 0 }));
  const next = nextOrgScopeKey(state, orgId);
  // React's pattern for state derived from a changing input: set during render, and
  // React re-renders before committing, so the subtree never mounts under the old key.
  if (next !== state) setState(next);
  return next.generation;
}
